/**
 * Utility functions for consistent date and time formatting across the app
 */

/**
 * Format a timestamp string to display local time
 * @param timestamp - ISO string timestamp
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted time string or error message
 */
export const formatTime = (
  timestamp: string | Date,
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' }
): string => {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp provided:', timestamp);
      return 'Invalid time';
    }
    
    // Use the user's local timezone
    return date.toLocaleTimeString([], options);
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
};

/**
 * Format a timestamp to show full date and time
 * @param timestamp - ISO string timestamp
 * @returns Formatted date and time string
 */
export const formatDateTime = (timestamp: string | Date): string => {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return date.toLocaleString();
  } catch (error) {
    console.error('Error formatting date/time:', error);
    return 'Invalid date';
  }
};

/**
 * Format a timestamp to show only the date
 * @param timestamp - ISO string timestamp
 * @returns Formatted date string
 */
export const formatDate = (timestamp: string | Date): string => {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format duration in seconds to human-readable format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "2m 30s" or "45s")
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 0 || isNaN(seconds)) {
    return '0s';
  }
  
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  
  if (minutes === 0) {
    return `${secs}s`;
  }
  
  return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
};

/**
 * Format timestamp with relative time for recent dates
 * @param timestamp - ISO string timestamp
 * @returns Formatted string like "Today, 2:30 PM", "Yesterday, 10:45 AM", or "Jan 15, 2:30 PM"
 */
export const formatRelativeTime = (timestamp: string | Date): string => {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const timeString = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit'
    });
    
    if (date.toDateString() === today.toDateString()) {
      return `Today, ${timeString}`;
    }
    
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${timeString}`;
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid date';
  }
};

/**
 * Format a timestamp to show long format date
 * @param timestamp - ISO string timestamp
 * @returns Formatted long date string (e.g., "Monday, January 1, 2024")
 */
export const formatLongDate = (timestamp: string | Date): string => {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting long date:', error);
    return 'Invalid date';
  }
};

/**
 * Get relative time string (e.g., "2 minutes ago", "Just now")
 * @param timestamp - ISO string timestamp
 * @returns Relative time string
 */
export const getRelativeTime = (timestamp: string | Date): string => {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    if (isNaN(date.getTime())) {
      return 'Unknown';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSecs < 10) {return 'Just now';}
    if (diffSecs < 60) {return `${diffSecs} seconds ago`;}
    if (diffMins === 1) {return '1 minute ago';}
    if (diffMins < 60) {return `${diffMins} minutes ago`;}
    if (diffHours === 1) {return '1 hour ago';}
    if (diffHours < 24) {return `${diffHours} hours ago`;}
    if (diffDays === 1) {return 'Yesterday';}
    if (diffDays < 7) {return `${diffDays} days ago`;}
    
    // For older dates, show the actual date
    return formatDate(date);
  } catch (error) {
    console.error('Error calculating relative time:', error);
    return 'Unknown';
  }
};